import React from 'react';
import { ColorfulHorizontalLine } from '@epcbuilder/lib/components/Lines';
import PageSection from '../PageSection';

const sectionContainer = 'mx-6 sm:mx-8 md:mx-6 xl:mx-auto xl:w-11/12 xl:max-w-screen-2xl';
const headingClass = 'text-md text-2xl font-bold leading-tight md:text-[48px] xl:text-[64px]';
const subheadingClass = 'font-jost mb-4 text-[20px] font-medium md:text-[24px]';
const paragraphClass = 'leading-[28px] sm:leading-[30px] xl:text-[18px] xl:leading-9';

const TickIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="size-6 lg:size-9" viewBox="0 0 36 36" fill="none">
    <path
      d="M1.22852 17.6416C6.24531 23.0697 11.1095 27.9564 15.7853 34.1369C20.8689 24.026 26.072 13.8799 34.6572 2.89213L32.3439 1.83252C25.0947 9.52049 19.4627 16.7978 14.5689 25.4463C11.1658 22.3806 5.66594 18.0424 2.30781 15.8135L1.22852 17.6416Z"
      fill="#70AA80"
    />
  </svg>
);

const benefits = [
  {
    title: 'Prevent damp & mould',
    desc: 'Reducing condensation buildup.',
  },
  {
    title: 'Protect your insulation',
    desc: 'Ensures it doesn’t become a trap for moisture.',
  },
  {
    title: 'Improve indoor air quality',
    desc: 'Removes pollutants and maintains a healthy environment.',
  },
];

const installationSteps = [
  {
    step: '01',
    title: 'Clearing the Area',
    desc: 'We begin by making sure the roof space is fully cleared and ready for installation.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="49" viewBox="0 0 48 49" fill="none">
        <path
          d="M47.2167 7.56333C48.2583 6.52166 48.2583 4.83 47.2167 3.78833C46.175 2.74666 44.4833 2.74666 43.4417 3.78833L27.4417 19.7883L24.55 16.8967C24.2 16.5467 23.7167 16.3467 23.2167 16.3467C22.175 16.3467 21.3333 17.1883 21.3333 18.23V20.655L30.3583 29.68H32.7833C33.825 29.68 34.6667 28.8383 34.6667 27.7967C34.6667 27.2967 34.4667 26.8133 34.1167 26.4633L31.225 23.5717L47.225 7.57166L47.2167 7.56333ZM28.425 32.4633L18.55 22.5883C14.9917 22.28 11.45 23.5633 8.9 26.1133L8.23333 26.78C6.375 28.6383 5.33333 31.155 5.33333 33.78C5.33333 34.3467 5.925 34.7133 6.43333 34.4633L10.6917 32.3383C11.1083 32.13 11.4833 32.68 11.1417 32.9967L0.608333 42.4633C0.225 42.8133 0 43.3133 0 43.8383C0 44.855 0.825 45.68 1.84167 45.68H16.2833C19.5167 45.68 22.6083 44.3967 24.9 42.1133C27.45 39.5633 28.725 36.0217 28.425 32.4633Z"
          fill="#E76F51"
        />
      </svg>
    ),
    color: '#E76F51',
  },
  {
    step: '02',
    title: 'Frame System Installation',
    desc: 'A strong and secure frame system is fitted to support the insulation materials.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="49" viewBox="0 0 48 49" fill="none">
        <g clipPath="url(#clip0_8118_10061)">
          <path
            d="M27.566 30.6907L31.808 26.4487L43.8 38.4407L39.558 42.6827L27.566 30.6907ZM35 20.3467C38.86 20.3467 42 17.2067 42 13.3467C42 12.1867 41.68 11.1067 41.18 10.1467L35.78 15.5467L32.8 12.5667L38.2 7.16674C37.24 6.66674 36.16 6.34674 35 6.34674C31.14 6.34674 28 9.48674 28 13.3467C28 14.1667 28.16 14.9467 28.42 15.6667L24.72 19.3667L21.16 15.8067L22.58 14.3867L19.76 11.5667L24 7.32674C22.875 6.20314 21.35 5.57202 19.76 5.57202C18.17 5.57202 16.645 6.20314 15.52 7.32674L8.44001 14.4067L11.26 17.2267H5.62001L4.20001 18.6467L11.28 25.7267L12.7 24.3067V18.6467L15.52 21.4667L16.94 20.0467L20.5 23.6067L5.68001 38.4267L9.92001 42.6667L32.68 19.9267C33.4 20.1867 34.18 20.3467 35 20.3467Z"
            fill="#F0935C"
          />
        </g>
        <defs>
          <clipPath id="clip0_8118_10061">
            <rect width="48" height="48" fill="white" transform="translate(0 0.34668)" />
          </clipPath>
        </defs>
      </svg>
    ),
    color: '#F0935C',
  },
  {
    step: '03',
    title: 'Insulation Fitting',
    desc: 'Mineral wool insulation rolls are installed between the frame sections to maximise thermal efficiency.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="49" viewBox="0 0 48 49" fill="none">
        <path
          d="M48 0.348633L37.8151 0.359019V10.5315H48V0.348633ZM36 0.360734L12 0.385641V10.5315H36V0.360734ZM10.1849 0.387557L0 0.398045V10.5315H10.1849V0.387557ZM0 12.3466V23.4391H23.0924V12.3466H0ZM24.9076 12.3466V23.4391H48V12.3466H24.9076ZM0 25.2542V36.3466H10.1849V25.2542H0ZM12 25.2542V36.3466H36V25.2542H12ZM37.8151 25.2542V36.3466H48V25.2542H37.8151ZM0 38.1617V48.3446L23.0924 48.3208V38.1617H0ZM24.9076 38.1617V48.3191L48 48.2952V38.1617H24.9076Z"
          fill="#EBBC67"
        />
      </svg>
    ),
    color: '#EBBC67',
  },
  {
    step: '04',
    title: 'Plastering and Finishing',
    desc: 'We apply plaster and a clean whitewash to give the area a fresh, finished look.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="49" viewBox="0 0 48 49" fill="none">
        <path
          d="M8 6.34009H40C41.1046 6.34009 42 7.23553 42 8.34009V18.3401C42 19.4447 41.1046 20.3401 40 20.3401H8C6.89544 20.3401 6 19.4447 6 18.3401V8.34009C6 7.23553 6.89544 6.34009 8 6.34009ZM12 24.3401H24C25.1046 24.3401 26 25.2355 26 26.3401V32.3401H28V44.3401H20V32.3401H22V28.3401H10C8.89544 28.3401 8 27.4447 8 26.3401V22.3401H12V24.3401ZM35.4644 27.8045L39 24.2691L42.5356 27.8045C44.4882 29.7571 44.4882 32.9231 42.5356 34.8757C40.583 36.8283 37.417 36.8283 35.4644 34.8757C33.5118 32.9231 33.5118 29.7571 35.4644 27.8045Z"
          fill="#70AA80"
        />
      </svg>
    ),
    color: '#70AA80',
  },
  {
    step: '05',
    title: 'Ventilation Installation',
    desc: 'Where needed, extractor fans or trickle vents are installed to maintain airflow and prevent damp.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="49" viewBox="0 0 48 49" fill="none">
        <g clipPath="url(#clip0_8118_10094)">
          <path
            d="M24.2438 0.34668C24.0844 0.34668 23.925 0.356055 23.7656 0.393555C15.75 1.94043 10.8375 9.93731 12.2344 18.0936C12.5063 19.6873 13.0219 21.1779 13.725 22.5373L3 21.3467H2.75625C1.2375 21.3467 0 22.5842 0 24.1029C0 24.2623 0.009375 24.4217 0.046875 24.5811C1.59375 32.5967 9.59063 37.5092 17.7469 36.1123C19.3406 35.8404 20.8312 35.3248 22.1906 34.6217L21 45.3467V45.5904C21 47.1092 22.2375 48.3467 23.7562 48.3467C23.9156 48.3467 24.075 48.3373 24.2344 48.2998C32.25 46.7529 37.1625 38.7561 35.7656 30.5998C35.4938 29.0061 34.9781 27.5154 34.275 26.1561L45 27.3467H45.2438C46.7625 27.3467 48 26.1092 48 24.5904C48 24.4311 47.9906 24.2717 47.9531 24.1123C46.4062 16.0967 38.4094 11.1842 30.2531 12.5811C28.6594 12.8529 27.1687 13.3686 25.8094 14.0717L27 3.34668V3.10293C27 1.58418 25.7625 0.34668 24.2438 0.34668ZM24 21.3467C24.7956 21.3467 25.5587 21.6628 26.1213 22.2254C26.6839 22.788 27 23.551 27 24.3467C27 25.1423 26.6839 25.9054 26.1213 26.468C25.5587 27.0306 24.7956 27.3467 24 27.3467C23.2044 27.3467 22.4413 27.0306 21.8787 26.468C21.3161 25.9054 21 25.1423 21 24.3467C21 23.551 21.3161 22.788 21.8787 22.2254C22.4413 21.6628 23.2044 21.3467 24 21.3467Z"
            fill="#228F81"
          />
        </g>
        <defs>
          <clipPath id="clip0_8118_10094">
            <rect width="48" height="48" fill="white" transform="translate(0 0.34668)" />
          </clipPath>
        </defs>
      </svg>
    ),
    color: '#228F81',
  },
];

const InternalWallInsulationPage = () => {
  return (
    <div className="flex min-h-screen flex-col gap-10 bg-white lg:gap-12 xl:gap-16">
      <div id="finance-intro" className={`${sectionContainer} mt-10 flex flex-col gap-4 md:mt-12 lg:gap-6`}>
        <h2 className={headingClass}>Internal Wall Insulation Information</h2>
        <p className="font-jost leading-7 text-[#516B75] md:text-[20px] md:leading-9 xl:text-[24px]">
          High-performance insulation to improve warmth, reduce energy bills, and enhance comfort
        </p>
      </div>

      <PageSection title="About SWIP" containerClassName={sectionContainer}>
        <div className="flex flex-col gap-4 xl:gap-6">
          <p className={paragraphClass}>
            SWIP LTD are a specialist system designer and material supplier of insulation solutions.
          </p>
        </div>
      </PageSection>

      <div className="bg-[#F8F9FA]">
        <div className={`${sectionContainer} m-6 flex flex-col gap-6 md:flex-row md:items-center`}>
          <div className="md:w-1/2">
            <h2 className="mb-2 font-[unset] text-lg font-black uppercase sm:mb-4 md:text-[22px] xl:text-2xl">
              About Mineral Wool
            </h2>
            <ColorfulHorizontalLine width="10rem" />
            <p className={`mr-6 mt-4 md:max-w-[552px] ${paragraphClass}`}>
              Mineral Wool is made from volcanic rock or glass that has been heated and spun into thin fibres, providing
              very good thermal and acoustic insulation. Mineral wool Room in Roof insulation is normally in the form of
              large rolls that are placed between a system frame attached to walls.
            </p>
          </div>
          <div className="flex flex-col gap-4 sm:gap-6 md:w-1/2 lg:px-6">
            {['swip-logo.png', 'wall-insulation.png'].map((img, i) => (
              <div
                key={i}
                className={`${
                  i === 0
                    ? 'h-[50px] max-w-[130px] bg-contain bg-left bg-no-repeat'
                    : 'h-[400px] max-w-[540px] rounded-[20px] bg-cover bg-center'
                }`}
                style={{
                  backgroundImage: `url('/images/roof-insulation/${img}')`,
                  backgroundColor: '#F8F9FA',
                  backgroundBlendMode: 'darken',
                }}
              />
            ))}
            <div>
              <p className="font-bold leading-7 xl:text-[18px]">SWIP</p>
              <p className="leading-7 text-[#516B75] xl:text-[18px]">Mineral Wood</p>
            </div>
          </div>
        </div>
      </div>

      <PageSection title="Ventilation & Your Home" containerClassName={sectionContainer}>
        <div className="flex flex-col gap-8 md:flex-row lg:gap-12 xl:gap-20">
          <div className="md:w-1/2">
            <p className={subheadingClass}>Why Ventilation Is Important</p>
            <p className={`mb-4 ${paragraphClass}`}>
              Proper ventilation plays a crucial role in maintaining a healthy home environment. It helps to:
            </p>
            <ul className="space-y-4">
              {benefits.map(({ title, desc }) => (
                <li key={title} className="flex items-center gap-3">
                  <div className="w-8">
                    <TickIcon />
                  </div>
                  <div>
                    <p className={`font-semibold ${paragraphClass}`}>{title}</p>
                    <p className={`text-[#516B75] ${paragraphClass}`}>{desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:w-1/2">
            <p className={subheadingClass}>Ventilation During Installation</p>
            <p className={paragraphClass}>
              When installing Internal Wall Insulation, additional ventilation may be required. This may include:
            </p>
            <ul className="list-inside list-disc space-y-2">
              <li className={paragraphClass}>Extractor fans - To enhance airflow and reduce humidity.</li>
              <li className={paragraphClass}>
                Trickle vents - Installed on windows to allow continuous air circulation.
              </li>
            </ul>
            <p className={paragraphClass}>
              The type of ventilation needed depends on your property, and our team will assess this during
              installation.
            </p>
          </div>
        </div>
      </PageSection>

      <PageSection title="Installation" containerClassName={sectionContainer}>
        <div className="mb-10 flex flex-col gap-10 lg:flex-row xl:mb-20 xl:gap-20">
          <div className="lg:w-1/2">
            <p className="font-jost mb-4 text-[20px] font-medium md:text-[24px]">Installation Steps</p>
            <div className="flex flex-col gap-4">
              {installationSteps.map(({ step, title, desc, icon, color }) => (
                <div
                  key={step}
                  className="flex w-full items-center gap-4 rounded-[10px] bg-white p-4 lg:rounded-[20px]"
                  style={{
                    boxShadow: '0px 0px 10px 0px rgba(21, 53, 66, 0.15)',
                    alignSelf: 'stretch',
                  }}
                >
                  <div className="mr-4 text-center">
                    <p className="text-[17px] font-extrabold text-[#748991] xl:text-[19px]">STEP</p>
                    <p
                      className="text-[40px] font-[900] leading-[50px] xl:text-[48px]"
                      style={{
                        color,
                      }}
                    >
                      {step}
                    </p>
                  </div>
                  <div className="flex-1">
                    <p className={`font-semibold ${paragraphClass}`}>{title}</p>
                    <p className={`text-[#516B75] ${paragraphClass}`}>{desc}</p>
                  </div>
                  <div className="ml-4 hidden text-2xl sm:flex">{icon}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:w-1/2">
            <p className="font-jost mb-4 text-[20px] font-medium md:text-[24px]">Installation Duration</p>
            <p className={`mb-6 ${paragraphClass}`}>
              It will take 3-5 days to install your Internal Wall Insulation, this includes any Ventilation.
            </p>
            <p className="font-jost mb-4 text-[20px] font-medium md:text-[24px]">Warranties & Guarantees</p>
            <ul className="space-y-4">
              <li className="flex items-center gap-3">
                <TickIcon />
                <p>Manufacturer Warranty</p>
              </li>
              <li className="flex items-center gap-3">
                <TickIcon />
                <p>Material & Workmanship Guarantee</p>
              </li>
              <li className="flex items-center gap-3">
                <TickIcon />
                <p>Insurance Backed Guarantee</p>
              </li>
            </ul>
            <img
              src="/images/roof-insulation/quality-mark-logo.png
            "
              alt="Quality Mark Logo"
              className="mt-4 w-full max-w-[500px] sm:w-[50%] lg:mt-6 lg:w-full"
            />
          </div>
        </div>
      </PageSection>
    </div>
  );
};

export default InternalWallInsulationPage;
